import { css } from '@emotion/react';
import { theme } from '@frontend/theme';

export type SizeProps = keyof typeof sizeStyles;

type ContainerStylesProps = { size: SizeProps; isInteractive: boolean };

export const sizeStyles = {
  xxs: css`
    font-size: ${theme.font.size.xs};
    font-weight: ${theme.font.weight.bold};
    height: 18px;
    min-height: 18px;
    min-width: 18px;
    width: 18px;
  `,
  xs: css`
    font-size: ${theme.font.size.xs};
    font-weight: ${theme.font.weight.bold};
    height: 24px;
    min-height: 24px;
    min-width: 24px;
    width: 24px;
  `,
  small: css`
    font-size: ${theme.font.size.small};
    font-weight: ${theme.font.weight.bold};
    height: 32px;
    min-height: 32px;
    min-width: 32px;
    width: 32px;
  `,
  medium: css`
    font-size: ${theme.font.size.large};
    font-weight: ${theme.font.weight.bold};
    height: 40px;
    min-height: 40px;
    min-width: 40px;
    width: 40px;
  `,
  large: css`
    font-size: ${theme.font.size.large};
    font-weight: ${theme.font.weight.bold};
    height: 48px;
    min-height: 48px;
    min-width: 48px;
    width: 48px;
  `,
  xl: css`
    font-size: ${theme.font.size.h2};
    font-weight: ${theme.font.weight.bold};
    height: 64px;
    min-height: 64px;
    min-width: 64px;
    width: 64px;
  `,
  xxl: css`
    font-size: ${theme.font.size.h2};
    font-weight: ${theme.font.weight.bold};
    height: 80px;
    min-height: 80px;
    min-width: 80px;
    width: 80px;
  `,
};

export const contentStyles = css`
  align-items: center;
  background-color: ${theme.colors.neutral10};
  border-radius: 50%;
  color: ${theme.colors.neutral90};
  display: flex;
  height: 100%;
  justify-content: center;
  line-height: 1;
  overflow: hidden;
  position: relative;
  text-transform: uppercase;
`;

const bottomIconPosition = {
  xs: css`
    bottom: -4px;
    right: -4px;
  `,
  small: css`
    bottom: -4px;
    right: 0px;
  `,
  medium: css`
    bottom: -5px;
    right: -5px;
  `,
  large: css`
    bottom: 0px;
    right: 0px;
  `,
  xl: css`
    bottom: 1px;
    right: 5px;
  `,
  xxl: css`
    bottom: 1px;
    right: 5px;
  `,
};

export const dotPosition = {
  xs: css`
    top: -4px;
    right: -4px;
  `,
  small: css`
    top: -5px;
    right: -2px;
  `,
  medium: css`
    top: -4px;
    right: -4px;
  `,
  large: css`
    top: 0px;
    right: -1px;
  `,
  xl: css`
    top: 1px;
    right: 3px;
  `,
  xxl: css`
    bottom: 1px;
    right: 5px;
  `,
};

export const containerStyles = ({ size, isInteractive }: ContainerStylesProps) => [
  css`
    position: relative;
    /* figure element has default margin styles that need to be removed */
    margin: 0;
    #avatar-bottom-icon > :first-of-type {
      border-radius: 50%;
      position: absolute;
      ${bottomIconPosition[size]}
    }
    #avatar-dot > :first-of-type {
      ${dotPosition[size]}
    }
  `,
  isInteractive &&
    css`
      &:focus {
        outline: 2px solid ${theme.colors.primary50};
        border-radius: 50%;
      }
      &:hover {
        cursor: pointer;
      }
    `,
];

export const aiAssistantStyles = css`
  border-radius: ${theme.borderRadius.medium};
  background: linear-gradient(
    -90deg,
    ${theme.colors.primary10},
    ${theme.colors.secondary.seaweed5},
    ${theme.colors.primary10},
    ${theme.colors.secondary.seaweed5}
  );
  background-size: 400% 400%;
  animation: gradient 5s ease infinite;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background: linear-gradient(
      -90deg,
      ${theme.colors.primary10},
      ${theme.colors.secondary.seaweed5},
      ${theme.colors.primary10},
      ${theme.colors.secondary.seaweed5}
    );
    background-size: 400% 400%;
    animation: gradient 5s ease infinite;
  }

  @keyframes gradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`;
